import isEmail from 'validator/lib/isEmail';
import { PARTICIPANT_SIGNER_TYPES, } from './create_consent_modal.types';
import { preferNonEmpty } from '../../utils/prefer_non_empty';
export const THIRD_PARTY_SIGNER_TYPE = 'third party';
export const getAssignableConsentResponseData = (body) => {
    if (Array.isArray(body)) {
        return body;
    }
    const data = body?.data;
    return Array.isArray(data) ? data : [];
};
/**
 * `POST /v2/routes/econsent/invite` answers with `{ ec__signed_doc, invites }` — the id of the
 * signed document the invites were issued against (see the route's `ec__invite_consent`
 * contract). Cortex wraps a route result in `{ object: 'result', data: … }`, so accept the
 * envelope and the bare payload, the same way getAssignableConsentResponseData does.
 */
export const getCreatedDocumentId = (body) => {
    const readSignedDocId = (payload) => {
        const signedDocId = payload?.ec__signed_doc;
        return typeof signedDocId === 'string' && signedDocId ? signedDocId : undefined;
    };
    return readSignedDocId(body) ?? readSignedDocId(body?.data);
};
const normalizeSignerType = (role) => role.ec__signer_type?.trim().toLowerCase() ?? '';
const getRuntimeFieldKey = (field) => {
    if (!field || typeof field !== 'object') {
        return undefined;
    }
    return field.ec__key?.trim() ?? '';
};
const hasDuplicate = (values) => new Set(values).size !== values.length;
export const getSignerRoleIdentity = (role) => role.ec__key?.trim() || role.ec__role?.trim() || '';
/**
 * SSP-4067: a template with at least one signer role but no digital fields is a valid
 * Print-to-Sign-only template — the role (typically Participant) is what an invite gets issued
 * against here (see buildInvites below), so it's still required even for PTS; only the digital
 * signature/data fields are eSign-specific and can be skipped. Mirrors
 * isPtsOnlyConsentDocument in libs.consent.common's publish_validation.ts (the authoring-side check) —
 * kept as a separate copy since this operates on the wire shape (TDocumentTemplate), not the
 * editor's ConsentDocument shape.
 */
export const isPtsOnlyDocumentTemplate = (template) => {
    const roles = template.ec__signer_roles ?? [];
    const requestedFields = [...(template.ec__requested_signatures ?? []), ...(template.ec__requested_data ?? [])];
    return roles.length > 0 && requestedFields.length === 0;
};
export const getTemplateValidationErrors = (template) => {
    const errors = [];
    const roles = template.ec__signer_roles ?? [];
    const ptsOnly = isPtsOnlyDocumentTemplate(template);
    if (!template._id) {
        errors.push('template_id_missing');
    }
    if (!template.ec__title) {
        errors.push('template_title_missing');
    }
    if (roles.length === 0) {
        errors.push('signer_roles_missing');
    }
    if (roles.some((role) => !role.ec__role?.trim())) {
        errors.push('signer_role_label_missing');
    }
    if (roles.some((role) => !normalizeSignerType(role))) {
        errors.push('signer_type_missing');
    }
    const roleIdentities = roles.map(getSignerRoleIdentity).filter(Boolean);
    if (roleIdentities.length !== roles.length || hasDuplicate(roleIdentities)) {
        errors.push('signer_role_identity_invalid');
    }
    const runtimeKeys = [...(template.ec__requested_signatures ?? []), ...(template.ec__requested_data ?? [])]
        .map(getRuntimeFieldKey)
        .filter((key) => key !== undefined);
    if (!ptsOnly && runtimeKeys.length === 0) {
        errors.push('runtime_fields_missing');
    }
    if (runtimeKeys.some((key) => !key)) {
        errors.push('runtime_field_key_missing');
    }
    if (hasDuplicate(runtimeKeys)) {
        errors.push('runtime_field_key_duplicate');
    }
    return errors;
};
export const isAssignableTemplate = (template) => getTemplateValidationErrors(template).length === 0;
export const getAssignableConsentTemplates = (assignableConsents = []) => {
    return assignableConsents
        .filter(({ ec__assignable }) => ec__assignable !== false)
        .map(({ ec__document_template = {}, ec__published_studio_template }) => ({
        ...ec__document_template,
        _id: ec__document_template._id ?? '',
        ec__title: ec__document_template.ec__title ?? '',
        ec__published: ec__published_studio_template?.ec__published,
        ec__published_studio_template,
        ec__requested_data: preferNonEmpty(ec__document_template.ec__requested_data, ec__published_studio_template?.ec__requested_data),
        ec__requested_signatures: preferNonEmpty(ec__document_template.ec__requested_signatures, ec__published_studio_template?.ec__requested_signatures),
        ec__signer_roles: preferNonEmpty(ec__document_template.ec__signer_roles, ec__published_studio_template?.ec__signer_roles),
    }))
        .filter(isAssignableTemplate);
};
export const isParticipantRole = (role) => PARTICIPANT_SIGNER_TYPES.includes(normalizeSignerType(role));
export const isThirdPartyRole = (role) => normalizeSignerType(role) === THIRD_PARTY_SIGNER_TYPE;
/**
 * An electronic invite registers every third-party role, so each one needs a
 * valid email even when the template leaves `ec__required` false. Printed
 * documents pass `collectsSignerInputs` false and skip that check.
 */
export const areSignerRolesValid = ({ template, roles, collectsSignerInputs, thirdPartyEmailsByRole, internalSignersByRole, }) => {
    if (!template) {
        return false;
    }
    if (!collectsSignerInputs) {
        return true;
    }
    return roles.every((role) => {
        const roleIdentity = getSignerRoleIdentity(role);
        if (isThirdPartyRole(role)) {
            return isEmail(thirdPartyEmailsByRole[roleIdentity]?.trim() ?? '');
        }
        return role.ec__required ? !!getInternalSignerEmail(internalSignersByRole[roleIdentity]) : true;
    });
};
export const getInternalSignerAccount = (signer) => {
    if (!signer) {
        return undefined;
    }
    const account = typeof signer.c_account === 'object' ? signer.c_account : undefined;
    return account ?? { email: signer.email, firstName: signer.firstName, lastName: signer.lastName };
};
export const getInternalSignerDisplayName = (signer) => {
    const account = getInternalSignerAccount(signer);
    const firstName = account?.firstName ?? account?.name?.first ?? '';
    const lastName = account?.lastName ?? account?.name?.last ?? '';
    const fullName = [firstName, lastName].filter(Boolean).join(' ').trim();
    const email = account?.email ?? '';
    if (fullName && email) {
        return `${fullName} - ${email}`;
    }
    return fullName || email || signer._id;
};
export const getInternalSignerEmail = (signer) => {
    const account = getInternalSignerAccount(signer);
    return account?.email;
};
export const buildInvites = ({ template, participantId, participantEmail, internalSignersByRole = {}, thirdPartyEmailsByRole = {}, signatureType, }) => {
    return (template.ec__signer_roles ?? [])
        .filter((role) => signatureType !== 'pts' || isParticipantRole(role))
        .map((role) => {
        const roleIdentity = getSignerRoleIdentity(role);
        const invite = {
            ec__signer_role: role.ec__role,
            ec__signer_type: role.ec__signer_type ?? '',
            ec__required: role.ec__required,
            ec__order: role.ec__order,
            ec__key: role.ec__key,
        };
        if (isParticipantRole(role)) {
            if (participantEmail) {
                invite.ec__email = participantEmail;
            }
            else {
                invite.c_public_user = participantId;
            }
        }
        else if (isThirdPartyRole(role)) {
            const thirdPartyEmail = thirdPartyEmailsByRole[roleIdentity]?.trim();
            if (thirdPartyEmail) {
                invite.ec__email = thirdPartyEmail;
            }
        }
        else {
            const internalSignerEmail = getInternalSignerEmail(internalSignersByRole[roleIdentity]);
            if (internalSignerEmail) {
                invite.ec__email = internalSignerEmail;
            }
        }
        return invite;
    })
        .filter((invite) => !!invite.ec__email || !!invite.c_public_user);
};
export const participantHasSameEmailAsInternalSigner = (participantEmail, internalSigner) => {
    if (!participantEmail || !internalSigner) {
        return false;
    }
    const signerEmail = getInternalSignerEmail(internalSigner);
    return !!signerEmail && participantEmail === signerEmail;
};
export const participantHasSameEmailAsSignerEmail = (participantEmail, signerEmail) => !!participantEmail && !!signerEmail && participantEmail === signerEmail.trim();
export const buildInvitePayload = (args) => {
    const payload = {
        ec__document_template: args.template._id,
        c_site: args.siteId,
        invites: buildInvites(args),
        ec__pts: args.signatureType === 'pts',
    };
    if (args.template.ec__published_studio_template?._id) {
        payload.ec__published_studio_template = args.template.ec__published_studio_template._id;
    }
    return payload;
};
export const templateHasInternalSignerRole = (template) => {
    if (!template?.ec__signer_roles?.length) {
        return false;
    }
    return template.ec__signer_roles.some((role) => !isParticipantRole(role));
};
